import { ITestRunSchema, testRunSchema, TestRunService } from '@/api/testRun';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { BulkTestCaseForm } from '@/components/testRun/BulkTestCaseForm';
import { TestRunForm } from '@/components/testRun/TestRunForm';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Dialog } from 'alurkerja-ui';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

export const CreateEditTestRun: React.FC = () => {
  const backNavigate = useBackNavigate();
  const { id, testRunId } = useParams();
  const [curTab, setCurTab] = useState(0);

  const { data: testRunData, isFetching: isTestRunFetching } =
    TestRunService.useGet(testRunId);
  const { data: optionsData, isFetching: isOptionsFetching } =
    TestRunService.useGetReleaseAndAssignee(id);
  const { data: testPlanData } = TestRunService.useGetTestPlan(id);
  const { mutate: create, isPending: isCreateLoading } =
    TestRunService.useCreate();
  const { mutate: update, isPending: isUpdateLoading } =
    TestRunService.useUpdate();

  const isFetching = isTestRunFetching || isOptionsFetching;
  const isLoading = isCreateLoading || isUpdateLoading;

  const form = useForm<ITestRunSchema>({
    resolver: zodResolver(testRunSchema),
    values: testRunData,
  });

  const submitAction = (data: ITestRunSchema) => {
    data.project_id = id;
    data.details = [];

    if (testRunId) {
      update(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Test run has been updated',
            callback: backNavigate,
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: 'Failed to update test run',
          });
        },
      });
    } else {
      create(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Test run has been created',
            callback: backNavigate,
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: 'Failed to create test run',
          });
        },
      });
    }
  };

  if (isFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <TabGroup className="tab-group" onChange={i => setCurTab(i)}>
      <TabList className="tab-list">
        <Tab className="tab-button">Test Run</Tab>
        <Tab className="tab-button">Test Case</Tab>
      </TabList>

      <FormContainer form={form} onSubmit={submitAction}>
        <Section title={testRunId ? 'Update Test Run' : 'Create Test Run'}>
          <FormFooter className='flex justify-between py-2'>
            <OutlineButton onClick={backNavigate}>Back</OutlineButton>

            <Button loading={isLoading}>
              {testRunId ? 'Update' : 'Create'}
            </Button>
          </FormFooter>
          <TabPanels>
            <TabPanel static>
              <div
                className={classNames({
                  hidden: curTab === 1,
                  block: curTab === 0,
                })}
              >
                <TestRunForm
                  optionsData={optionsData}
                  optionsPlan={testPlanData}
                />
              </div>
            </TabPanel>

            <TabPanel static>
              <div
                className={classNames({
                  hidden: curTab === 0,
                  block: curTab === 1,
                })}
              >
                <BulkTestCaseForm
                  testCaseIds={testRunData?.test_case_ids}
                  moduleIds={testRunData?.module_ids}
                />
              </div>
            </TabPanel>
          </TabPanels>
        </Section>
      </FormContainer>
    </TabGroup>
  );
};
